/**
 * Mobile Teams Page
 * Shows all user's teams across all leagues
 * Smart detection: 1 team = auto-navigate, multiple = show picker
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dynastyTheme } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';

function MobileTeams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllTeams();
  }, []);

  const fetchAllTeams = async () => {
    setLoading(true);
    setError('');

    try {
      // Get all user's leagues
      const leaguesResponse = await leaguesAPI.getMyLeagues();
      
      if (leaguesResponse.success && leaguesResponse.leagues) {
        // Collect all teams from all leagues
        const allTeams = leaguesResponse.leagues.map(league => ({
          leagueId: league.league_id,
          leagueName: league.league_name,
          teamName: league.team_name,
          teamId: league.team_id
        }));

        setTeams(allTeams);

        // SMART DETECTION: If only 1 team, auto-navigate to team home
        if (allTeams.length === 1) {
          const team = allTeams[0];
          navigate(`/leagues/${team.leagueId}/team`);
        }
      } else {
        setError('No teams found');
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleTeamClick = (team) => {
    navigate(`/leagues/${team.leagueId}/team`);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${dynastyTheme.classes.bg.dark}`}>
        <div className="text-center">
          <div className="text-4xl mb-4 animate-bounce">👥</div>
          <p className={`text-lg ${dynastyTheme.classes.text.neutralLighter}`}>
            Loading your teams...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${dynastyTheme.classes.bg.dark}`}>
        <div className="text-center px-6">
          <div className="text-4xl mb-4">❌</div>
          <p className={`text-lg ${dynastyTheme.classes.text.white} mb-2`}>
            {error}
          </p>
          <button
            onClick={fetchAllTeams}
            className={`${dynastyTheme.utils.getComponent('button', 'primary', 'md')} mt-4`}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`min-h-screen ${dynastyTheme.classes.bg.dark}`}
      style={{ paddingTop: '20px' }}
    >
      {/* Header */}
      <div className="px-6 mb-6">
        <h1 className={`text-3xl font-bold ${dynastyTheme.classes.text.white} mb-2`}>
          👥 My Teams
        </h1>
        <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>
          {teams.length} {teams.length === 1 ? 'team' : 'teams'} across all leagues
        </p>
      </div>

      {/* Teams List */}
      <div className="px-6 space-y-3">
        {teams.length === 0 ? (
          <div className={`${dynastyTheme.components.card.base} p-6 text-center`}>
            <div className="text-5xl mb-4">🏆</div>
            <h2 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-2`}>
              No Teams Yet
            </h2>
            <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>
              Join a league to get started!
            </p>
          </div>
        ) : (
          teams.map((team, index) => (
            <button
              key={index}
              onClick={() => handleTeamClick(team)}
              className={`${dynastyTheme.components.card.interactive} w-full p-4 text-left`}
            >
              {/* Team Info */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className={`text-lg font-semibold ${dynastyTheme.classes.text.white} mb-1`}>
                    {team.teamName}
                  </h3>
                  <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter} flex items-center gap-2`}>
                    <span>🏆</span>
                    {team.leagueName}
                  </p>
                </div>
                <div className={`${dynastyTheme.classes.text.primary} text-2xl`}>
                  →
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

export default MobileTeams;
